'use client';

import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import { EPlatform } from '@/utils/enums/platform-enum';
import { checkFooterConfig } from '@/utils/tools';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { FC, useEffect, useMemo } from 'react';
const FooterMobile = dynamic(() => import('./Footer.m'), { ssr: true });
const FooterPC = dynamic(() => import('./Footer.pc'), { ssr: true });

interface FooterProps {
  navList: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  deviceType: EPlatform;
}

const Footer: FC<FooterProps> = ({ navList, deviceType }) => {
  const { trackEvent } = useReporting();
  const setIsShowFooter = useStore((state) => state.setIsShowFooterNav);
  const setIsShowFooterSubscribe = useStore((state) => state.setIsShowFooterSubscribe);
  const path = usePathname();
  const isMobile = deviceType !== EPlatform.WEB;
  const { cnName } = useRouteMetadata();

  const footerConfig = useMemo(() => checkFooterConfig(path, isMobile), [path, isMobile]);

  useEffect(() => {
    setIsShowFooter(footerConfig.isShowFooterNav);
    setIsShowFooterSubscribe(footerConfig.isShowFooterSubscribe);
  }, [footerConfig, setIsShowFooter, setIsShowFooterSubscribe]);

  const SDSocialMediaClick = (btn: string) => {
    trackEvent(
      {
        eventName: 'Socialmedia_Click',
        properties: {
          btn_name: btn,
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  const SDBottomNavTabClick = (
    first_tab_name: string,
    second_tab_name?: string,
    first_tab_index?: number,
    second_tab_index?: number,
  ) => {
    trackEvent(
      {
        eventName: 'BottomNavTab_Click',
        properties: {
          first_tab_name,
          second_tab_name,
          first_tab_index,
          second_tab_index,
          page_name: cnName,
          url_path: window.location.href,
        },
      },
      [ProviderType.SENSORS],
    );
  };

  return footerConfig.isShowFooterNav ? (
    <>
      <WebOnly>
        <FooterPC
          navList={navList}
          SDSocialMediaClick={SDSocialMediaClick}
          SDBottomNavTabClick={SDBottomNavTabClick}
          isShowSubscribe={footerConfig.isShowFooterSubscribe}
          deviceType={deviceType}
        />
      </WebOnly>
      <WapOnly hideApp>
        <FooterMobile
          navList={navList}
          SDSocialMediaClick={SDSocialMediaClick}
          SDBottomNavTabClick={SDBottomNavTabClick}
          isShowSubscribe={footerConfig.isShowFooterSubscribe}
          deviceType={deviceType}
        />
      </WapOnly>
    </>
  ) : null;
};

export default Footer;
